/** Lightweight classnames helper (no `clsx` dependency). */
export function cn(...args) {
  return args.flat(Infinity).filter(Boolean).join(' ')
}

/** Format ISO date to '15 Apr 2026' style. Falls back to '—' for empties. */
export function formatDate(iso, { withTime = false } = {}) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  if (!withTime) return date
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${date} · ${time}`
}

/** Format a Date or yyyy-mm-dd into yyyy-mm-dd (for backend date fields). */
export function toIsoDate(value) {
  if (!value) return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

/** Compute age in years from DOB (yyyy-mm-dd). */
export function calculateAge(dob) {
  if (!dob) return null
  const today = new Date()
  const birth = new Date(dob)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1
  return age
}

/** Initials for an avatar (e.g. "Harshini P" → "HP"). */
export function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')
}

/** Make a snake_case enum value human readable: 'tobacco_chewing' → 'Tobacco chewing' */
export function humanize(value = '') {
  if (!value) return ''
  const s = String(value).replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Strip empty strings / empty arrays / nulls so we never send junk to backend. */
export function cleanPayload(obj) {
  if (Array.isArray(obj)) {
    const arr = obj.map(cleanPayload).filter((v) => v !== undefined)
    return arr.length ? arr : undefined
  }
  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    const out = {}
    for (const [k, v] of Object.entries(obj)) {
      const cleaned = cleanPayload(v)
      if (cleaned !== undefined && cleaned !== '' && cleaned !== null) {
        if (Array.isArray(cleaned) && cleaned.length === 0) continue
        out[k] = cleaned
      }
    }
    return Object.keys(out).length ? out : undefined
  }
  return obj
}

/** Skip a section entirely if all its fields are empty. */
export function hasMeaningfulData(section) {
  if (!section) return false
  return Object.values(section).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'string') return v.trim().length > 0
    if (typeof v === 'boolean') return v === true
    return v != null
  })
}

/** Mobile-friendly preview: "9677893709" → "96778 93709" */
export function formatMobile(num = '') {
  const digits = String(num).replace(/\D/g, '')
  if (digits.length === 10) return `${digits.slice(0, 5)} ${digits.slice(5)}`
  return num
}

/** ₹1,234.50 — used in billing and inventory MRPs. */
export function formatRupees(amount, { decimals = 2 } = {}) {
  const n = Number(amount)
  if (!Number.isFinite(n)) return '₹0.00'
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Number of days from now to a yyyy-mm-dd date (positive = future). */
export function daysFromToday(iso) {
  if (!iso) return null
  const target = new Date(iso)
  if (Number.isNaN(target.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.round((target - today) / (1000 * 60 * 60 * 24))
}

/** Relative time: "2h ago", "3d ago", "just now". */
export function timeAgo(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diff = Math.round((Date.now() - d.getTime()) / 1000)
  if (diff < 30) return 'just now'
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return formatDate(iso)
}
